/**
 * StrategyPanel.jsx — Persistent trading strategy notes, saved to Cloudflare KV via worker.
 *
 * Unlike SessionNotes (daily / expires at market close), StrategyPanel holds long-lived
 * strategy text — your edge, rules, framework, watch-levels — that persists across days.
 *
 * Storage: GET/PUT /strategy  →  WATCHLIST KV key: strategy:<username>
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { strategyGet, strategyPut } from '../../lib/api';

// ── API helpers ──────────────────────────────────────────────────────────────
const fetchStrategy = strategyGet;
const putStrategy   = (text) => strategyPut(text);

// ── Component ────────────────────────────────────────────────────────────────
export default function StrategyPanel() {
  const [text,    setText]    = useState('');
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState('');
  const [sync,    setSync]    = useState('loading'); // 'loading' | 'ok' | 'saving' | 'err'
  const [saved,   setSaved]   = useState(false);
  const saveTimer = useRef(null);
  const taRef     = useRef(null);

  // Load on mount
  useEffect(() => {
    fetchStrategy()
      .then(d => { setText(d.text || ''); setSync('ok'); })
      .catch(() => setSync('err'));
  }, []);

  // Persist to worker
  const persist = useCallback(async (val) => {
    setSync('saving');
    try {
      await putStrategy(val);
      setSync('ok');
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch {
      setSync('err');
    }
  }, []);

  // ── Edit flow ──────────────────────────────────────────────────────────────
  const startEdit = () => {
    setDraft(text);
    setEditing(true);
    setSaved(false);
    setTimeout(() => taRef.current?.focus(), 30);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft('');
    clearTimeout(saveTimer.current);
  };

  const saveEdit = useCallback(() => {
    const trimmed = draft.trim();
    setText(trimmed);
    setEditing(false);
    setDraft('');
    clearTimeout(saveTimer.current);
    persist(trimmed);
  }, [draft, persist]);

  const onKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); saveEdit(); }
    if (e.key === 'Escape') cancelEdit();
  };

  // ── Styles ─────────────────────────────────────────────────────────────────
  const mono = { fontFamily: "'IBM Plex Mono', monospace" };
  const label = { ...mono, fontSize: 9, textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--muted)' };
  const btn = (variant = 'default') => ({
    ...mono, fontSize: 10, fontWeight: 600, border: 'none', borderRadius: 4,
    padding: '4px 11px', cursor: 'pointer', letterSpacing: '.04em',
    ...(variant === 'primary'  ? { background: 'var(--accent)', color: '#fff' } : {}),
    ...(variant === 'default'  ? { background: 'var(--bg-hover)', color: 'var(--muted)', border: '1px solid var(--border)' } : {}),
    ...(variant === 'danger'   ? { background: 'transparent', color: 'var(--down)', border: '1px solid var(--down-soft)' } : {}),
  });

  const syncColor = { loading: 'var(--muted)', ok: 'var(--up)', saving: 'var(--warn)', err: 'var(--down)' }[sync];
  const syncLabel = { loading: '⟳ loading', ok: saved ? '✓ saved' : '', saving: '⟳ saving…', err: '✗ error' }[sync];

  // ── Render: loading ────────────────────────────────────────────────────────
  if (sync === 'loading') {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ ...mono, fontSize: 11, color: 'var(--muted)' }}>⟳ loading…</span>
      </div>
    );
  }

  // ── Render: editing ────────────────────────────────────────────────────────
  if (editing) {
    return (
      <div style={{ padding: '10px 12px', height: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={label}>✎ EDIT STRATEGY</div>
        <textarea
          ref={taRef}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={'Your trading framework, rules, and edge.\n\ne.g. Trade with trend only. No chasing. Max 3 positions. Cut losers fast. Let winners breathe. SPY > 50MA = long bias only.'}
          style={{
            flex: 1, background: 'var(--bg)', border: '1px solid var(--border-strong)',
            borderRadius: 6, color: 'var(--text)', ...mono, fontSize: 11,
            lineHeight: 1.65, padding: '10px 12px', resize: 'none', outline: 'none',
          }}
          onFocus={e => e.target.style.borderColor = 'var(--accent)'}
          onBlur={e  => e.target.style.borderColor = 'var(--border-strong)'}
        />
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button onClick={saveEdit} disabled={!draft.trim()} style={btn('primary')}>✓ Save</button>
          <button onClick={cancelEdit} style={btn('default')}>Cancel</button>
          <span style={{ ...mono, fontSize: 9, color: 'var(--faint)', marginLeft: 'auto' }}>⌘↵ save · Esc cancel</span>
        </div>
      </div>
    );
  }

  // ── Render: empty ──────────────────────────────────────────────────────────
  if (!text) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, textAlign: 'center', padding: 16 }}>
        <div style={{ fontSize: 22, opacity: 0.35 }}>📐</div>
        <div style={{ ...mono, fontSize: 10, color: 'var(--muted)', lineHeight: 1.6 }}>
          No strategy saved yet.<br />Document your rules, edge, and framework.
        </div>
        <button onClick={startEdit} style={{ ...btn('primary'), padding: '6px 18px', fontSize: 11 }}>
          + Write Strategy
        </button>
      </div>
    );
  }

  // ── Render: view ───────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '10px 12px', height: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <span style={label}>📐 STRATEGY</span>
        {syncLabel && (
          <span style={{ ...mono, fontSize: 9, color: syncColor }}>{syncLabel}</span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          <button onClick={startEdit} style={{ ...btn('default'), padding: '3px 9px', fontSize: 9 }}>✎ Edit</button>
          <button
            onClick={() => { if (confirm('Clear strategy?')) { setText(''); persist(''); } }}
            style={{ ...btn('danger'), padding: '3px 9px', fontSize: 9 }}
          >✕</button>
        </div>
      </div>

      {/* Content */}
      <div style={{
        flex: 1, overflowY: 'auto', background: 'var(--bg)',
        border: '1px solid var(--border)', borderRadius: 6, padding: '10px 12px',
      }}>
        <pre style={{
          margin: 0, ...mono, fontSize: 11, lineHeight: 1.7,
          color: 'var(--text)', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
          {text}
        </pre>
      </div>
    </div>
  );
}
